import React, { useContext, useMemo } from "react";
import * as Mui from "@mui/material";

import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import FormInput from "../common/FormInput";
import AutoComplete, { AutoCompleteOption } from "../common/AutoComplete";
import VehicleEntranceService from "./service/VehicleEntranceService";
import { EnumStatusReasons } from "../vehicle-availability/service/VehicleAvailabilityService";
import { useEffect, useState } from "react";
import useSnackbar from "../../hooks/useSnackbar";
import { ContextOperation } from "../../context/OperationContext";

export interface FormInputs {
  logsticLocation: string | null;
  position: string | null;
  batteryLevel: string | null;
  fuelTank: string | null;
  km: string | null;
}

const VehicleEntrancePlateMigration = () => {
  const { setLoading, userLocations } = useContext(ContextOperation);

  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const location = useLocation();
  const t = useOperationTranslation();

  const form = useForm<FormInputs>({
    defaultValues: {
      position: null,
      batteryLevel: null,
      fuelTank: null,
      km: null
    },
  });
  const [chipLabel, setChipLabel] = useState<any>(null);
  var reg = /^[0-9\b]+$/;
  const vehicleData: any = location.state;

  useEffect(() => {
    if (vehicleData === null) {
      navigate(`/operation/transfer/vehiclesEntrance`);
    } else {
      setChipLabel(`${vehicleData.vehicleStatus}`);
    }
  }, [vehicleData]);

  const locations = useMemo<AutoCompleteOption[]>(() => {
    return userLocations.map((loc) => ({
      label: loc.descr || "",
      value: loc.key || ""
    })) || [];
  }, [userLocations]);

  const onSubmit = (data: FormInputs) => {
    const { plate } = vehicleData;

    const entranceLocationKey: string = (locations.find((item: any) => item.value === form.watch("logsticLocation"))?.value || "") as string;

    const entranceLocationId = entranceLocationKey.split('-')[0];
    const entranceStationType = entranceLocationKey.split('-')[1];

    const postData = {
      plate: plate,
      entranceStationId: entranceLocationId,
      entranceStationType,
      km: Number(form.watch("km")),
      ...(showTermicLevelInput && {
        fuelLevel: Number(form.watch("fuelTank")) || 0,
      }),
      ...(showElettricBatteryInput && {
        batteryLevel: Number(form.watch("batteryLevel")) || 0,
      }),
    };

    setLoading(true);
    VehicleEntranceService.add
      .vehicleFirstCheckin(vehicleData.tenant, postData).then(
        () => {
          setLoading(false);
          snackbar.show("success", t("vehicleEntrance.search.success"));
          navigate(`/operation/transfer/vehiclesEntrance`);
        },
        (reject) => {
          setLoading(false);
          const status = reject.response.status;

          let error: any = { type: "error", message: "" };

          if (status === 409 && (reject.response.data.subCode === 13 || reject.response.data.subCode === 16)) {
            error = {
              type: "error",
              message: t(
                "vehicleEntrance.search.errors.vehicleFieldsMissing"
              ),
            };
          } else if (status === 409) {
            error = {
              type: "warning",
              message: t("vehicleEntrance.search.errors.alreadyExist"),
            };
          }

          if (status === 400) {
            error = {
              type: "error",
              message: t("vehicleEntrance.search.errors.badRequest"),
            };
          }

          if (status === 500) {
            error = {
              type: "error",
              message: t("vehicleEntrance.search.errors.genericError"),
            };
          }

          snackbar.show(error.type, error.message);
        }
      );
  };

  const batteryValueInputLength = (value: string | null) => {
    const length = (value || "").length;

    if (length === 1) {
      return 10;
    }

    if (length === 2) {
      return 20;
    }

    if (length === 3) {
      return 29;
    }

    return 60;
  };

  const showElettricBatteryInput =
    vehicleData.enginePowerCode === "E" ||
    vehicleData.enginePowerCode === "HB" ||
    vehicleData.enginePowerCode === "HD";

  const showTermicLevelInput =
    vehicleData.enginePowerCode === "D" ||
    vehicleData.enginePowerCode === "G" ||
    vehicleData.enginePowerCode === "B" ||
    vehicleData.enginePowerCode === "HB" ||
    vehicleData.enginePowerCode === "HD";

  const kmValueInputLength = (value: string | null) => {
    const baseWidth = 20;
    const widthPerCharacter = 10;
    const length = (value || "").length;
    return baseWidth + (length * widthPerCharacter) + 'px';
  };
  return (
    vehicleData && (
      <>
        <Mui.Box paddingBottom="16px">
          <Mui.Grid container spacing={{ md: 2 }} p={{ md: 2 }}>
            <Mui.Grid item md={6} xs={12}>
              <Mui.Card>
                <Mui.CardContent>
                  <Mui.Stack gap={2} alignItems="center" mb={2}>
                    <Mui.Skeleton
                      variant="rectangular"
                      width="150px"
                      height="150px"
                    />
                    <Mui.Typography
                      variant="h4"
                      mb="10px"
                      textAlign={{ md: "left", xs: "center" }}
                    >
                      {`${vehicleData.model.brand.descr} ${vehicleData.model.descr}`}
                    </Mui.Typography>
                    <Mui.Chip
                      label={chipLabel}
                      color={
                        vehicleData.vehicleStatus ===
                        EnumStatusReasons.AVAILABLE
                          ? "info"
                          : "warning"
                      }
                    />
                  </Mui.Stack>

                  <Mui.Box sx={{ display: "flex", gap: "10px" }} pt="20px">
                    <Mui.Typography variant="body1">
                      {t("vehicleEntrance.status.plate")}:
                    </Mui.Typography>
                    <Mui.Typography variant="h5">
                      {vehicleData.plate}
                    </Mui.Typography>
                  </Mui.Box>

                  <Mui.Box pt="20px">
                    {/* <Mui.Typography variant="body1">
                      {t("vehicleEntrance.entranceLogisticLabel")}:
                    </Mui.Typography> */}
                    <AutoComplete
                      fullWidth
                      options={locations}
                      value={form.watch("logsticLocation") || ""}
                      onChange={(newStatusValue) => {
                        form.setValue(
                          "logsticLocation",
                          newStatusValue as string
                        );
                      }}
                      renderInput={(params) => (
                        <Mui.TextField
                          {...params}
                          variant="standard"
                          size="small"
                          {...form.register("logsticLocation", {
                            required: t(
                              "vehicleEntrance.status.lmsErrorMessage"
                            ),
                          })}
                          label={t("vehicleEntrance.entranceLogisticLabel")}
                          error={Boolean(
                            form.formState.errors?.logsticLocation?.message?.toString()
                          )}
                          helperText={
                            Boolean(
                              form.formState.errors?.logsticLocation?.message?.toString()
                            )
                              ? t("vehicleExit.status.driverError")
                              : ""
                          }
                          placeholder={t(
                            "vehicleEntrance.entranceLogisticLabel"
                          )}
                        />
                      )}
                    />
                  </Mui.Box>
                </Mui.CardContent>
              </Mui.Card>
            </Mui.Grid>

            <Mui.Grid item md={6} xs={12}>
              <Mui.Card>
                <Mui.CardContent>
                  <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Mui.Grid container spacing={0}>
                      <FormInput
                        label={t("vehicleEntrance.status.kmLabel")}
                        placeholder={t("vehicleEntrance.status.kmLabel")}
                        formInstance={form}
                        {...form.register("km", {
                          required: t("vehicleEntrance.status.driverError"),
                        })}
                        type="text"
                        onChange={(event) => {
                          if (reg.test(event) || event === "") {
                            form.setValue("km", event);
                          }
                        }}
                        fullWidth
                        error={Boolean(form.formState.errors["km"])}
                        helperText={form.formState.errors[
                          "km"
                        ]?.message?.toString()}
                        sx={{
                          input: {
                            width: kmValueInputLength(form.getValues("km")),
                            marginBottom: 0,
                            paddingBottom: 0,
                          },
                        }}
                        InputProps={{
                          style: { paddingBottom: 10 },
                          endAdornment: (
                            <Mui.InputAdornment position="start">
                              {form.getValues("km") && "KM"}
                            </Mui.InputAdornment>
                          ),
                        }}
                      />

                      {showElettricBatteryInput && (
                        <>
                          <Mui.TextField
                            variant="standard"
                            label={t("vehicleEntrance.status.batteryLabel")}
                            placeholder={t("vehicleEntrance.search.exBattery")}
                            fullWidth
                            {...form.register("batteryLevel", {
                              required: t("vehicleEntrance.status.driverError"),
                            })}
                            value={form.watch("batteryLevel")}
                            onChange={(event) => {
                              if (
                                reg.test(event.target.value) ||
                                event.target.value === ""
                              ) {
                                if (event.target.value.length > 3) {
                                  return form.setValue(
                                    "batteryLevel",
                                    form.watch("batteryLevel")
                                  );
                                }
                                form.setValue(
                                  "batteryLevel",
                                  event.target.value
                                );
                              } else {
                                form.setValue("batteryLevel", "");
                              }
                            }}
                            helperText={
                              Number(form.getValues("batteryLevel")) > 100
                                ? t("vehicleEntrance.search.batteryMaxValue")
                                : form.formState.errors[
                                    "batteryLevel"
                                  ]?.message?.toString()
                            }
                            error={Boolean(
                              Number(form.getValues("batteryLevel")) > 100
                                ? true
                                : form.formState.errors["batteryLevel"]
                            )}
                            style={{ marginTop: 20 }}
                            sx={{
                              input: {
                                width: batteryValueInputLength(
                                  form.getValues("batteryLevel")
                                ),
                                marginBottom: 0,
                                paddingBottom: 0,
                              },
                            }}
                            InputProps={{
                              style: { paddingBottom: 10 },
                              endAdornment: (
                                <Mui.InputAdornment position="start">
                                  {form.getValues("batteryLevel") && "%"}
                                </Mui.InputAdornment>
                              ),
                            }}
                          />
                        </>
                      )}

                      {showTermicLevelInput && (
                        <>
                          <Mui.TextField
                            variant="standard"
                            label={t("vehicleEntrance.status.fuelLabel")}
                            placeholder={t("vehicleEntrance.search.exFuel")}
                            fullWidth
                            {...form.register("fuelTank", {
                              required: t("vehicleEntrance.status.driverError"),
                            })}
                            value={form.watch("fuelTank")}
                            onChange={(event) => {
                              if (
                                reg.test(event.target.value) ||
                                event.target.value === ""
                              ) {
                                if (event.target.value.length > 1) {
                                  return form.setValue(
                                    "fuelTank",
                                    form.watch("fuelTank")
                                  );
                                }
                                form.setValue("fuelTank", event.target.value);
                              } else {
                                form.setValue("fuelTank", "");
                              }
                            }}
                            helperText={
                              Number(form.watch("fuelTank")) > 8
                                ? t("vehicleEntrance.search.fuelMaxValue")
                                : form.formState.errors[
                                    "fuelTank"
                                  ]?.message?.toString()
                            }
                            error={Boolean(
                              Number(form.watch("fuelTank")) > 8
                                ? true
                                : form.formState.errors["fuelTank"]
                            )}
                            sx={{
                              input: {
                                width: batteryValueInputLength(
                                  form.getValues("fuelTank")
                                ),
                                marginBottom: 0,
                                paddingBottom: 0,
                              },
                            }}
                            style={{ marginTop: 20 }}
                            InputProps={{
                              style: { paddingBottom: 10 },
                              endAdornment: (
                                <Mui.InputAdornment position="start">
                                  {form.watch("fuelTank") && "/8"}
                                </Mui.InputAdornment>
                              ),
                            }}
                          />
                        </>
                      )}

                      <Mui.Grid item xs={12} mt={2}>
                        <Mui.Button
                          fullWidth
                          variant="contained"
                          style={{ color: "white", backgroundColor: "#d12c93" }}
                          type="submit"
                        >
                          {t("vehicleEntrance.confirm.confirmStatus")}
                        </Mui.Button>
                      </Mui.Grid>
                    </Mui.Grid>
                  </form>
                </Mui.CardContent>
              </Mui.Card>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Box>
      </>
    )
  );
};
export default VehicleEntrancePlateMigration;
